// GA4 for app.nexmail.ai. Uses the same measurement ID as nexmail.ai, so the
// shared .nexmail.ai cookie carries a visitor's marketing-site session into
// the app and a sign-up is credited to the page that sent them.
//
// GA only exists here to record sign_up. It loads from the sign-up and sign-in
// pages, stays disabled on every other page (several app URLs carry
// single-use tokens), and never reports query strings.

type Gtag = (...args: unknown[]) => void;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: Gtag;
    [disableFlag: `ga-disable-${string}`]: boolean | undefined;
  }
}

const MEASUREMENT_ID = 'G-YS8XRHH5RG';
const PRODUCTION_HOST = 'app.nexmail.ai';
// TEMPORARY: lets the setup be checked locally. Local hits go to the same GA4
// property in debug mode (visible under Admin > DebugView). Remove once verified.
const LOCAL_HOSTS = ['localhost', '127.0.0.1'];
const DISABLE_FLAG = `ga-disable-${MEASUREMENT_ID}` as const;

const TRACKED_PATHS = ['/auth/sign-up', '/auth/sign-in', '/auth/onboarding'];

export function isTrackedPath(pathname: string): boolean {
  const path = pathname.replace(/\/+$/, '') || '/';
  return TRACKED_PATHS.includes(path);
}

/** Origin + path only: query strings can hold promo codes and install state. */
export function pageLocation(href: string): string {
  const url = new URL(href);
  return `${url.origin}${url.pathname}`;
}

/**
 * Turns GA on or off for the page being navigated to. Runs before the URL
 * changes, so GA's own history-change page_view already sees the flag; gtag
 * checks it before each hit, so untracked pages send nothing.
 */
export function syncAnalyticsPage(pathname: string) {
  if (!window.gtag) return;
  const tracked = isTrackedPath(pathname);
  window[DISABLE_FLAG] = !tracked;
  if (tracked) window.gtag('set', { page_location: `${window.location.origin}${pathname}` });
}

export function initAnalytics() {
  const { hostname } = window.location;
  const isLocal = LOCAL_HOSTS.includes(hostname);
  if ((hostname !== PRODUCTION_HOST && !isLocal) || window.gtag) return;
  if (!isTrackedPath(window.location.pathname)) return;

  window[DISABLE_FLAG] = false;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // gtag.js only recognizes the Arguments object, not a plain array.
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', MEASUREMENT_ID, {
    page_location: pageLocation(window.location.href),
    ...(isLocal && { debug_mode: true }),
  });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(script);
}

export function trackSignUp(method: 'email' | 'google') {
  window.gtag?.('event', 'sign_up', { method });
}
